<style lang="scss" scoped>
    .code-block-container > pre {
        margin: 0;
    }
    .controls {
        display: flex;
        justify-content: flex-end;
    }
</style>

<template>
    <div class="markdown-content">
        <template v-for="(block, index) in parsedBlocks">
            <div
                v-if="block.type === 'text'"
                :key="'text-' + index"
                v-html="block.content"></div>
            <div
                v-else-if="block.type === 'code'"
                :key="'code-' + index"
                class="code-block-container">
                <template v-if="block.language === 'sql'">
                    <prism-editor
                        v-model="codeBlocks[index]"
                        class="code-editor"
                        :highlight="highlighter" />
                </template>
                <pre
                    v-else
                    :class="'my-4 language-' + block.language"><code
                        :class="'language-' + block.language"
                v-html="block.content"></code></pre>
                <div class="mb-6 controls">
                    <v-btn
                        text
                        x-small
                        @click="copyToClipboard(codeBlocks[index])">
                        <v-icon left>
                            mdi-content-copy
                        </v-icon>
                        {{ $t('copy') }}
                    </v-btn>
                    <v-btn
                        v-if="block.language === 'sql'"
                        text
                        x-small
                        color="primary"
                        :disabled="!selectedDatasource || areDatasourcesLoading"
                        @click="runQuery(codeBlocks[index])">
                        <v-icon left>
                            mdi-play-box
                        </v-icon>
                        {{ $t('copilot.runQuery') }}
                        <span v-if="queryType === 'api'">
                            → {{ selectedDatasource?.name | truncate(20) }}
                        </span>
                    </v-btn>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex'
    import { marked } from 'marked'
    import Prism from 'prismjs'
    import { PrismEditor } from 'vue-prism-editor'
    import { isCopilotAgent } from 'services/copilot.js'

    export default {
        name: 'MarkdownRenderer',
        components: {
            PrismEditor,
        },
        props: {
            content: {
                type: String,
                required: true,
            },
        },
        data() {
            return {
                codeBlocks: [],
            }
        },
        computed: {
            ...mapGetters([
                'selectedAgent',
                'selectedDatasource',
                'areDatasourcesLoading'
            ]),
            queryType() {
                return isCopilotAgent('ama', this.selectedAgent?.id) ? 'api' : 'msg'
            },
            parsedBlocks() {
                const blocks = []
                const content = this.content.trim()
                const resArr = content.split('```') || []
                const renderer = this.initializeMarkedRenderer()

                resArr.forEach((item, index) => {
                    if ((content.startsWith('```') && index % 2 !== 0) || index % 2 === 0) {
                        // Regular text block
                        blocks.push({
                            type: 'text',
                            content: marked.parse(item, { renderer, }),
                        })
                    } else {
                        // Code block
                        const codeArr = item.split('\n')
                        let lang = codeArr[0]
                        lang = Prism.languages[lang] ? lang : 'markup'
                        codeArr.shift()
                        const code = codeArr.join('\n')
                        const highlightedCode = Prism.highlight(code, Prism.languages[lang], lang)

                        blocks.push({
                            type: 'code',
                            language: lang,
                            content: highlightedCode,
                            rawCode: code,
                        })
                    }
                })

                return blocks
            },
        },
        watch: {
            parsedBlocks: {
                handler() {
                    this.codeBlocks = this.parsedBlocks.map((block) => block.rawCode?.trim() || '')
                },
                deep: true,
                immediate: true,
            },
        },
        methods: {
            ...mapActions(['copyToClipboard']),
            initializeMarkedRenderer() {
                const renderer = new marked.Renderer()
                renderer.link = ({ href, title, tokens, }) => {
                    const text = renderer.parser.parseInline(tokens)
                    const cleanHref = this.cleanUrl(href)
                    if (cleanHref === null) {
                        return text
                    }
                    href = cleanHref
                    let out = `<a target="_blank" href="${href}"`
                    if (title) {
                        out += ` title="${escape(title)}"`
                    }
                    out += `>${text}</a>`
                    return out
                }
                return renderer
            },
            cleanUrl(href) {
                try {
                    return encodeURI(href).replace(/%25/g, '%')
                }
                catch {
                    return null
                }
            },
            runQuery(code) {
                this.$emit('run-query', { type: this.queryType, code, })
            },
            highlighter(code) {
                return Prism.highlight(code, Prism.languages.sql, 'sql')
            },
        },
    }
</script>
